import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
  type CategoryUsageRow,
  type CompetitorReviewSnapshot,
  type GbpCategoryAuditResult,
  type MissingCategoryRow,
  type ReviewsComparison,
  type YourCategoryRow,
  getGbpCategoryAudit,
  runGbpCategoryAudit,
} from "../lib/api";
import { Button, Field, TextInput } from "../components/Form";
import { updateClient, extractGbpCid } from "../lib/api";
import { useToast } from "../components/Toast";
import type { WorkspaceContext } from "./ClientWorkspacePage";

/**
 * GBP Categories tab — competitor category audit.
 *
 * Pulls the top 20 local-pack competitors for the client's primary GBP
 * category in their city, then visualizes:
 *   1. Your categories vs competitor trends (left/right two-column)
 *   2. Competitor categories you're missing (with competitor counts)
 *   3. Full distribution of competitor category usage (table with bars)
 *
 * Data source today is a stub. Real DataForSEO Local Finder integration is
 * gated on the DATAFORSEO_LOGIN/DATAFORSEO_PASSWORD env vars. The UI works
 * identically either way — only the numbers change.
 */
export default function GbpCategoriesTab() {
  const { client, refreshClient } = useOutletContext<WorkspaceContext>();
  const toast = useToast();

  const initialCity = parseCityFromAddress(client.businessAddress);
  const [primaryCategory, setPrimaryCategory] = useState(
    client.gbpPrimaryCategory ?? ""
  );
  const [city, setCity] = useState(initialCity);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [audit, setAudit] = useState<GbpCategoryAuditResult | null>(null);
  const [ranAt, setRanAt] = useState<string | null>(null);
  // fix707: GBP CID field (manual + auto-extract).
  const [cid, setCid] = useState(client.gbpCid ?? "");
  const [cidSaving, setCidSaving] = useState(false);
  const [cidExtracting, setCidExtracting] = useState(false);
  const [cidNote, setCidNote] = useState<string | null>(null);

  // Load any cached audit result on mount so the user sees their last run
  useEffect(() => {
    let cancelled = false;
    getGbpCategoryAudit(client.id)
      .then((data) => {
        if (cancelled) return;
        if (data.result) {
          setAudit(data.result);
          setRanAt(data.ranAt ?? data.result.ranAt);
        }
      })
      .catch((err) => console.error("Load cached audit failed:", err));
    return () => {
      cancelled = true;
    };
  }, [client.id]);

  // Re-sync the form when the client switches or its primary category updates
  useEffect(() => {
    setPrimaryCategory(client.gbpPrimaryCategory ?? "");
    setCity(parseCityFromAddress(client.businessAddress));
  }, [client.id, client.gbpPrimaryCategory, client.businessAddress]);

  async function handleSaveCid() {
    setCidSaving(true);
    setCidNote(null);
    try {
      await updateClient(client.id, { gbpCid: cid.trim() || null });
      await refreshClient();
      toast.show("CID saved.", { kind: "success" });
    } catch (err) {
      toast.show(err instanceof Error ? err.message : "Save failed.", { kind: "error" });
    } finally {
      setCidSaving(false);
    }
  }

  async function handleExtractCid() {
    setCidExtracting(true);
    setCidNote(null);
    try {
      const res = await extractGbpCid(client.id);
      if (res.cid) {
        setCid(res.cid);
        setCidNote(
          `Found a CID via ${
            res.source === "hex_place_id" ? "the map place ID" : "a map link"
          }. Review it, then Save.`
        );
      } else {
        setCidNote(
          res.reason ??
            "No CID found on the site. Enter it manually from the listing's Google Maps URL."
        );
      }
    } catch (err) {
      setCidNote(
        err instanceof Error ? err.message : "Could not scan the website."
      );
    } finally {
      setCidExtracting(false);
    }
  }

  async function handleRun() {
    if (!primaryCategory.trim() || !city.trim()) {
      setError("Primary GBP Category and City are required.");
      return;
    }
    setError(null);
    setRunning(true);
    try {
      const data = await runGbpCategoryAudit(client.id, {
        primaryCategory: primaryCategory.trim(),
        city: city.trim(),
      });
      setAudit(data.result);
      setRanAt(data.result.ranAt);

      // Backend auto-saved rating + review count if it found the client's
      // own listing in the Maps response. Refresh the workspace client
      // object so the Reviews comparison card below reads the fresh values.
      const yl = data.result.yourListing;
      if (yl && (yl.rating !== null || yl.reviewCount !== null)) {
        await refreshClient();
        const parts: string[] = [];
        if (yl.rating !== null) parts.push(`${yl.rating.toFixed(1)} stars`);
        if (yl.reviewCount !== null)
          parts.push(`${yl.reviewCount.toLocaleString()} reviews`);
        toast.show(
          `Found your listing: "${yl.matchedTitle}" (${parts.join(", ")}). ` +
            `Updated Review Data in Client Settings.`,
          { kind: "success" }
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Audit failed.");
    } finally {
      setRunning(false);
    }
  }

  const gapsFound = audit?.missingCategories.length ?? 0;
  const competitorCategoryCount = audit?.categoryUsage.length ?? 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-bg-card border border-border rounded-lg p-5 shadow-card flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-bg-subtle border border-border flex items-center justify-center text-text-muted shrink-0">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="12 2 2 7 12 12 22 7 12 2" />
            <polyline points="2 17 12 22 22 17" />
            <polyline points="2 12 12 17 22 12" />
          </svg>
        </div>
        <div>
          <h1 className="text-text font-semibold text-xl">
            Google Business Profile Audit
          </h1>
          <p className="text-text-muted text-sm mt-0.5">
            Compare your Google categories against direct competitors in your
            area. Add any categories you're missing — each one is a new way
            Google can match you to searches.
          </p>
          {audit && (
            <p className="text-text-subtle text-xs mt-1.5 flex items-center gap-2 flex-wrap">
              <span className="text-success">✓ Audited</span>
              <span className="text-text-subtle">·</span>
              <span className="text-warning">{gapsFound} gaps found</span>
              <span className="text-text-subtle">·</span>
              <span>{competitorCategoryCount} competitor categories</span>
            </p>
          )}
        </div>
      </div>

      {/* fix707: GBP CID — first thing under the title. */}
      <div className="bg-bg-card border border-border rounded-lg p-5 shadow-card">
        <h2 className="text-text font-semibold mb-1">Google Business Profile CID</h2>
        <p className="text-text-muted text-sm mb-3">
          The numeric ID of this client's Google Maps listing. Used for reviews
          widgets, map links, and schema. Paste it, or try auto-extracting it
          from the client's website.
        </p>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[220px]">
            <Field label="CID">
              <TextInput
                value={cid}
                onChange={setCid}
                placeholder="e.g. 12848505555338857927"
              />
            </Field>
          </div>
          <Button
            onClick={handleExtractCid}
            disabled={cidExtracting || cidSaving}
            variant="secondary"
          >
            {cidExtracting ? "Scanning…" : "Extract from website"}
          </Button>
          <Button
            onClick={handleSaveCid}
            disabled={cidSaving || cidExtracting}
          >
            {cidSaving ? "Saving…" : "Save"}
          </Button>
        </div>
        {cidNote && (
          <p className="text-text-subtle text-xs mt-2">{cidNote}</p>
        )}
        <p className="text-text-subtle text-xs mt-2">
          Auto-extract finds a CID when the site links to Google Maps with a
          <code className="mx-1">cid=</code>value or an embedded place ID. Plain
          embed iframes don't expose one — enter it manually in that case (open
          the listing on Google Maps; the URL contains the CID).
        </p>
      </div>

      {/* Audit setup card */}
      <div className="bg-bg-card border border-border rounded-lg p-5 shadow-card">
        <h2 className="text-text font-semibold mb-1">Audit setup</h2>
        <p className="text-text-muted text-sm mb-4">
          Compares against the top 30 Google Maps results for this category in
          this city.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-1">
          <Field
            label="Primary category"
            hint={
              client.gbpPrimaryCategory
                ? "ⓘ Auto-filled from GBP profile"
                : "Set your primary category in Client Settings to auto-fill this."
            }
          >
            <TextInput
              value={primaryCategory}
              onChange={setPrimaryCategory}
              placeholder="e.g. Dentist"
            />
          </Field>
          <Field
            label="City"
            hint={
              initialCity
                ? "Auto-filled from your business address."
                : "Set your business address in Client Settings to auto-fill this."
            }
          >
            <TextInput
              value={city}
              onChange={setCity}
              placeholder="e.g. Chicago, IL"
            />
          </Field>
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="primary"
            onClick={handleRun}
            disabled={running || !primaryCategory.trim() || !city.trim()}
          >
            {running ? "↻ Running Audit..." : "🔍 Run audit"}
          </Button>
          {error && <span className="text-danger text-sm">{error}</span>}
          {ranAt && !running && !error && (
            <span className="text-text-subtle text-xs">
              Last run {formatTimestamp(ranAt)}
            </span>
          )}
        </div>

        {audit?.message && (
          <p
            className={`text-xs mt-3 ${
              audit.stubbed ? "text-warning" : "text-text-muted"
            }`}
          >
            {audit.stubbed ? "⚠ " : "✓ "}
            {audit.message}
          </p>
        )}
      </div>

      {/* Results */}
      {audit && (
        <>
          <HighImpactGapsCard missingCategories={audit.missingCategories} />
          <YourCurrentCategoriesCard
            yourCategories={audit.yourCategories}
            competitorCount={audit.competitorCount}
          />
          <FullCompetitorReportCard
            categoryUsage={audit.categoryUsage}
            competitorCount={audit.competitorCount}
            yourCategoryNames={
              new Set(audit.yourCategories.map((c) => c.name.toLowerCase()))
            }
          />
          {audit.reviewsComparison && (
            <ReviewsVsCompetitorsCard
              reviews={audit.reviewsComparison}
              yourRating={client.averageRating}
              yourReviewCount={client.totalReviewCount}
            />
          )}
        </>
      )}
    </div>
  );
}

// =========================================================================
// Section: High-impact gaps
// =========================================================================
function HighImpactGapsCard({
  missingCategories,
}: {
  missingCategories: MissingCategoryRow[];
}) {
  if (missingCategories.length === 0) {
    return (
      <div className="bg-bg-card border border-border rounded-lg p-5 shadow-card">
        <h2 className="text-text font-semibold mb-1">High-impact gaps</h2>
        <p className="text-text-subtle text-sm">
          No gaps — competitors aren't using any categories you don't already
          have. Nice.
        </p>
      </div>
    );
  }

  // Sort by competitor count desc and scale the bars to the top gap.
  const sorted = [...missingCategories].sort(
    (a, b) => b.competitorCount - a.competitorCount
  );
  const maxCount = sorted[0]?.competitorCount || 1;

  return (
    <div className="bg-bg-card border border-border rounded-lg p-5 shadow-card">
      <div className="flex items-start justify-between gap-3 mb-1">
        <h2 className="text-text font-semibold">High-impact gaps</h2>
        <span className="text-accent text-xs font-medium bg-accent-subtle rounded-full px-2.5 py-1 shrink-0">
          ✦ {sorted.length} worth considering
        </span>
      </div>
      <p className="text-text-muted text-sm mb-4">
        Categories your direct competitors use that you don't. Add the ones that
        genuinely fit on Google Business Profile, then re-paste your category
        list in Client Settings.
      </p>

      <div className="space-y-2.5">
        {sorted.map((row) => (
          <div key={row.name} className="flex items-center gap-3">
            <span className="text-text font-medium text-sm flex-1 min-w-0 truncate">
              {row.name}
            </span>
            <div className="h-2 bg-bg-subtle rounded-full overflow-hidden w-40 shrink-0">
              <div
                className="h-full bg-accent transition-all"
                style={{ width: `${(row.competitorCount / maxCount) * 100}%` }}
              />
            </div>
            <span className="text-text-muted text-xs w-24 text-right shrink-0">
              {row.competitorCount} competitor
              {row.competitorCount === 1 ? "" : "s"}
            </span>
          </div>
        ))}
      </div>

      {/* How to add these callout */}
      <div className="flex gap-2 bg-accent-subtle border border-accent/20 rounded-lg px-3 py-2.5 mt-4">
        <span className="text-accent text-sm leading-none mt-0.5">ⓘ</span>
        <p className="text-text-muted text-xs">
          <span className="font-semibold text-text">How to add these:</span>{" "}
          Open your Google Business Profile, add the categories that genuinely
          fit, then re-paste your full list in{" "}
          <span className="font-medium text-text">
            Client Settings → Google Business Profile
          </span>{" "}
          to stay in sync.
        </p>
      </div>
    </div>
  );
}

// =========================================================================
// Section: Your current categories
// =========================================================================
function YourCurrentCategoriesCard({
  yourCategories,
  competitorCount,
}: {
  yourCategories: YourCategoryRow[];
  competitorCount: number;
}) {
  const validatedCount = yourCategories.filter(
    (c) => !c.isPrimary && c.alsoUsedByCompetitors
  ).length;
  const additionalCount = yourCategories.filter((c) => !c.isPrimary).length;

  return (
    <div className="bg-bg-card border border-border rounded-lg p-5 shadow-card">
      <h2 className="text-text font-semibold mb-1">Your current categories</h2>
      <p className="text-text-muted text-sm mb-4">
        What's on your live Google Business Profile. {validatedCount} of{" "}
        {additionalCount} additional categories validated by competitor use.
      </p>

      {yourCategories.length === 0 ? (
        <p className="text-text-subtle text-sm">
          No GBP categories set. Add them in Client Settings.
        </p>
      ) : (
        <div className="space-y-2">
          {yourCategories.map((row) => (
            <div key={row.name} className="flex items-center gap-2">
              <span
                className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wide w-20 text-center shrink-0 ${
                  row.isPrimary
                    ? "bg-accent text-white"
                    : "bg-accent-subtle text-accent border border-accent/20"
                }`}
              >
                {row.isPrimary ? "Primary" : "Additional"}
              </span>
              <span className="text-text font-medium flex-1 min-w-0 truncate">
                {row.name}
              </span>
              {row.isPrimary ? (
                <span className="text-text-subtle text-xs shrink-0">
                  {competitorCount} competitors share this
                </span>
              ) : row.alsoUsedByCompetitors ? (
                <span className="text-success text-xs shrink-0">
                  ✓ Competitors use this too
                </span>
              ) : (
                <span className="text-warning text-xs shrink-0">
                  ⚠ Unique to you — verify it's the right fit
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// =========================================================================
// Section: Full competitor category report (filterable table)
// =========================================================================
function FullCompetitorReportCard({
  categoryUsage,
  competitorCount,
  yourCategoryNames,
}: {
  categoryUsage: CategoryUsageRow[];
  competitorCount: number;
  yourCategoryNames: Set<string>;
}) {
  const [filter, setFilter] = useState("");
  const [hidden, setHidden] = useState(false);

  if (categoryUsage.length === 0) return null;

  const rows = categoryUsage.filter((r) =>
    r.category.toLowerCase().includes(filter.trim().toLowerCase())
  );

  return (
    <div className="bg-bg-card border border-border rounded-lg p-5 shadow-card">
      <div className="flex items-start justify-between gap-3 mb-1">
        <h2 className="text-text font-semibold">Full competitor category report</h2>
        <div className="flex items-center gap-2 shrink-0">
          <div className="w-44">
            <TextInput value={filter} onChange={setFilter} placeholder="🔍 Filter…" />
          </div>
          <button
            type="button"
            onClick={() => setHidden(!hidden)}
            className="text-text-muted hover:text-text text-xs whitespace-nowrap"
          >
            {hidden ? "▸ Show" : "▾ Hide"} all {categoryUsage.length}
          </button>
        </div>
      </div>
      <p className="text-text-muted text-sm mb-3">
        Every category at least one competitor uses. Useful for spotting
        long-tail opportunities.
      </p>

      {!hidden && (
        <div className="overflow-x-auto max-h-96 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-bg-card">
              <tr className="text-text-muted text-xs uppercase tracking-wide">
                <th className="text-left py-2 pr-4 font-medium">Category</th>
                <th className="text-left py-2 pr-4 font-medium w-1/3">
                  Competitor Usage
                </th>
                <th className="text-left py-2 pr-4 font-medium w-16">Count</th>
                <th className="text-left py-2 font-medium w-20">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const isYours = yourCategoryNames.has(row.category.toLowerCase());
                return (
                  <tr key={row.category} className="border-t border-border-subtle">
                    <td className="py-2 pr-4 text-text">{row.category}</td>
                    <td className="py-2 pr-4">
                      <div className="h-2 bg-bg-subtle rounded-full overflow-hidden max-w-xs">
                        <div
                          className="h-full bg-accent transition-all"
                          style={{ width: `${row.percent}%` }}
                        />
                      </div>
                    </td>
                    <td className="py-2 pr-4 text-text-muted">{row.count}</td>
                    <td className="py-2">
                      {isYours ? (
                        <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-success-bg text-success border border-success-border">
                          Yours
                        </span>
                      ) : (
                        <span className="text-text-subtle text-xs">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-text-subtle text-xs mt-3">
        N = {competitorCount} competitors analyzed.
      </p>
    </div>
  );
}

// =========================================================================
// Helpers
// =========================================================================
function parseCityFromAddress(address: string | null): string {
  if (!address) return "";
  // "11510 Katy Fwy, Houston, TX 77043" -> "Houston, TX"
  // fix282: addresses may be stored across two lines (street on line 1, city on
  // line 2, per the two-line reflow), so the suite and city are separated by a
  // NEWLINE, not a comma ("...Rd #270\nCharlotte, NC 28277"). Treat newlines as
  // segment separators too, otherwise "Rd #270" and "Charlotte" get mashed
  // together and the city comes out as "...#270Charlotte".
  const parts = address
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length < 2) return "";
  const city = parts[parts.length - 2] ?? "";
  const stateZip = parts[parts.length - 1] ?? "";
  const stateMatch = stateZip.match(/^([A-Z]{2})/);
  if (city && stateMatch) {
    return `${city}, ${stateMatch[1]}`;
  }
  return city;
}

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

// =========================================================================
// Reviews vs Competitors Card
// =========================================================================
// Computes percentile-style comparisons against the top 20 ranked
// competitors from the same Maps API call (no extra cost). Two views:
//
//   1. Median snapshot — your rating + reviews vs the median of the top 20.
//      Easier-to-grok "are we above/below typical?" framing.
//   2. Top 10 by rank — the actual local-pack winners with their stars +
//      review counts. Lets the user see who they're competing against.
//
// Hidden when reviewsComparison is null (stub data, or DataForSEO didn't
// return rating fields).
// =========================================================================
function ReviewsVsCompetitorsCard({
  reviews,
  yourRating,
  yourReviewCount,
}: {
  reviews: ReviewsComparison;
  yourRating: number | null;
  yourReviewCount: number | null;
}) {
  // Sort mode toggle for the Top 10 table. Defaults to "reviews" since
  // that's a closer proxy to what users see in Google Search local pack
  // (which weights review count heavily). Maps rank is the original
  // backend order (geographic centroid weight). User can flip between.
  const [sortMode, setSortMode] = useState<"rank" | "reviews">("reviews");

  // Compute per-axis comparison labels.
  const ratingDelta =
    yourRating !== null && reviews.medianRating !== null
      ? yourRating - reviews.medianRating
      : null;
  const reviewDelta =
    yourReviewCount !== null && reviews.medianReviewCount !== null
      ? yourReviewCount - reviews.medianReviewCount
      : null;

  // Format a delta nicely. "+0.3", "-0.2", "0.0".
  const fmtRatingDelta = (d: number) =>
    `${d > 0 ? "+" : ""}${d.toFixed(1)}`;
  const fmtReviewDelta = (d: number) =>
    `${d > 0 ? "+" : ""}${Math.round(d).toLocaleString()}`;

  // Color logic: positive = green (above median), negative = red (below).
  const ratingDeltaColor =
    ratingDelta === null
      ? "text-text-subtle"
      : ratingDelta > 0.05
      ? "text-success"
      : ratingDelta < -0.05
      ? "text-danger"
      : "text-text-muted";
  const reviewDeltaColor =
    reviewDelta === null
      ? "text-text-subtle"
      : reviewDelta > 0
      ? "text-success"
      : reviewDelta < 0
      ? "text-danger"
      : "text-text-muted";

  return (
    <div className="bg-bg-card border border-border rounded-lg p-5 shadow-card mb-4">
      <h3 className="text-accent font-semibold mb-3">
        Reviews vs Competitors
      </h3>
      <p className="text-xs text-text-muted mb-4">
        Compared against the top 20 closest competitors in the local pack
        ({reviews.sampleSize} with rating data).
      </p>

      {/* Median snapshot — two big stat tiles */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="bg-bg-subtle border border-border-subtle rounded-md px-4 py-3">
          <p className="text-[11px] uppercase tracking-wider text-text-subtle mb-1">
            Star Rating
          </p>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-semibold text-text">
              {yourRating !== null ? yourRating.toFixed(1) : "—"}
            </span>
            <span className="text-text-subtle text-sm">you</span>
          </div>
          <p className="text-xs text-text-muted mt-1">
            Median: {reviews.medianRating !== null ? reviews.medianRating.toFixed(1) : "—"}
            {ratingDelta !== null && (
              <>
                {" "}·{" "}
                <span className={`font-medium ${ratingDeltaColor}`}>
                  {fmtRatingDelta(ratingDelta)}
                </span>
              </>
            )}
          </p>
        </div>
        <div className="bg-bg-subtle border border-border-subtle rounded-md px-4 py-3">
          <p className="text-[11px] uppercase tracking-wider text-text-subtle mb-1">
            Review Count
          </p>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-semibold text-text">
              {yourReviewCount !== null
                ? yourReviewCount.toLocaleString()
                : "—"}
            </span>
            <span className="text-text-subtle text-sm">you</span>
          </div>
          <p className="text-xs text-text-muted mt-1">
            Median:{" "}
            {reviews.medianReviewCount !== null
              ? Math.round(reviews.medianReviewCount).toLocaleString()
              : "—"}
            {reviewDelta !== null && (
              <>
                {" "}·{" "}
                <span className={`font-medium ${reviewDeltaColor}`}>
                  {fmtReviewDelta(reviewDelta)}
                </span>
              </>
            )}
          </p>
        </div>
      </div>

      {/* Top 10 — toggle between Maps rank and review count.
          The backend order is Maps rank; we re-sort client-side when
          "By Reviews" is selected. */}
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] uppercase tracking-wider text-text-subtle">
          Top 10 in Local Pack
        </p>
        <div className="inline-flex rounded-md border border-border overflow-hidden text-xs">
          <button
            type="button"
            onClick={() => setSortMode("rank")}
            className={`px-3 py-1 transition ${
              sortMode === "rank"
                ? "bg-accent text-white"
                : "bg-bg-card text-text-muted hover:text-text"
            }`}
          >
            By Maps Rank
          </button>
          <button
            type="button"
            onClick={() => setSortMode("reviews")}
            className={`px-3 py-1 transition border-l border-border ${
              sortMode === "reviews"
                ? "bg-accent text-white"
                : "bg-bg-card text-text-muted hover:text-text"
            }`}
          >
            By Reviews
          </button>
        </div>
      </div>
      <div className="border border-border-subtle rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-bg-subtle">
            <tr className="text-text-muted text-[11px] uppercase tracking-wider">
              <th className="text-left px-3 py-2 w-10">#</th>
              <th className="text-left px-3 py-2">Business</th>
              <th className="text-right px-3 py-2 w-24">Rating</th>
              <th className="text-right px-3 py-2 w-24">Reviews</th>
            </tr>
          </thead>
          <tbody>
            {/* Defensive accessor: cached audits from before fix3 had this
                field as `top5`. Reading from either shape unblocks loading
                without forcing a re-run. New audits use `topRanked`. */}
            {(() => {
              const list =
                reviews.topRanked ??
                (reviews as unknown as { top5?: CompetitorReviewSnapshot[] })
                  .top5 ??
                [];
              if (sortMode === "reviews") {
                // Sort by reviewCount descending; entries with no review
                // count fall to the bottom in their original Maps order.
                return [...list].sort((a, b) => {
                  const ra = a.reviewCount ?? -1;
                  const rb = b.reviewCount ?? -1;
                  return rb - ra;
                });
              }
              // "rank" — use the order the backend sent (Maps rank).
              return list;
            })().map((c, i) => (
              <tr
                key={`${c.rank}-${c.title}`}
                className="border-t border-border-subtle"
              >
                <td className="px-3 py-2 text-text-muted">{i + 1}</td>
                <td className="px-3 py-2 text-text">{c.title}</td>
                <td className="px-3 py-2 text-right text-text">
                  {c.rating !== null ? c.rating.toFixed(1) : "—"}
                </td>
                <td className="px-3 py-2 text-right text-text-muted">
                  {c.reviewCount !== null
                    ? c.reviewCount.toLocaleString()
                    : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(yourRating === null || yourReviewCount === null) && (
        <p className="text-xs text-text-muted mt-3">
          Tip: set your Average Rating and Total Review Count in Client
          Settings → Reviews to see your comparison metrics here.
        </p>
      )}
    </div>
  );
}
